import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Checkbox,
  Row,
  Col,
  DatePicker,
  Select,
  Space,
  App,
  Spin,
  Tag,
  Collapse,
  Divider,
  Typography
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  FilePdfOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  SendOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import dayjs from 'dayjs';
import { formatUnit } from '../utils/units';
import WarehouseQuantityField from '../modules/warehouse/WarehouseQuantityField';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  getWarehouseLineUnit,
  type WarehouseQuantityPatch,
} from '../modules/warehouse/warehouseQuantityConversion';
import { formatProductDimensions } from '../utils/productDimensions';
import { formatConversionValue } from '../utils/dimensionConversion';
import {
  WZItem,
  WZStatus,
  CreateWZDto,
  UpdateWZDto
} from '../types/wz';
import {
  WZDocumentDto,
  attachWZItemDimensions,
  buildRecipientFieldsFromCustomer,
  mapOrderProductsToWZItems,
  normalizeWZDocument,
  prepareWZItemsForSave
} from '../dto/wz';
import { OrderOptionDto, normalizeOrderOptions } from '../dto/order';
import { canManageWZDocuments } from '../modules/warehouse/warehouseDocumentAccessPolicy';
import { getEditableWZStatusOptions, isWZEditable } from '../utils/wzEditRules';
import ProductNameCombobox from '../components/inventory/ProductNameCombobox';
import CustomerNameCombobox from '../components/inventory/CustomerNameCombobox';
import { buildWzVariantAutofill } from '../utils/wzVariantSelection';
import type { ProductVariant } from '../types/pz';
import { fetchOrderTransports } from '../modules/orders/api/orderTransportApi';
import type { TransportRouteSummaryDto } from '../dto/transport';
import { buildWZTransportFormPatch, buildWZTransportOption, mergeWZTransportOptions } from '../utils/wzTransportSelection';
import { WZItemCard, WZItemField, WZItemsList } from './WZItemsView';
import {
  WZ_QUANTITY_INVALID_CODE,
  WZ_QUANTITY_MAX,
} from '../utils/warehouseQuantity';
import './WZItemsView.css';

const { Option } = Select;
const { TextArea } = Input;
const createEmptyItem = (): Partial<WZItem> => ({ productName: '', quantity: 1, unit: 'pcs' });

const formatLineDimensions = (item: Partial<WZItem>) =>
  formatProductDimensions({
    width: item.width,
    height: item.height,
    length: item.length,
    thickness: item.thickness,
    productCategoryDimensionDisplayProfile: item.productCategoryDimensionDisplayProfile ?? 'GENERIC',
  }) || '-';

const WZDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['wz', 'common', 'transport']);
  const { message } = App.useApp();
  const [form] = Form.useForm();

  const isNewDocument = id === 'new';
  const queryParams = new URLSearchParams(location.search);
  const requestedEditMode = queryParams.get('mode') === 'edit' || isNewDocument;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wzDocument, setWzDocument] = useState<WZDocumentDto | null>(null);
  const [items, setItems] = useState<Partial<WZItem>[]>([createEmptyItem()]);
  const [orders, setOrders] = useState<OrderOptionDto[]>([]);
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [linkedTransports, setLinkedTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [deliveryDifferent, setDeliveryDifferent] = useState(false);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageWZDocuments(currentUser);
  const canEditSalePrices = authService.canEditCustomerSalePrices();
  const canEditPurchasePrices = authService.canEditWarehouseDocumentPurchasePrices();
  const canDownloadWZPdf = authService.canDownloadWZDocumentPdf();
  const currentUserDisplayName = currentUser?.name || '';
  const canEditDocument = canEdit && (isNewDocument || isWZEditable(wzDocument?.status));
  const isEditMode = requestedEditMode && canEditDocument;
  const selectedOrderId = Form.useWatch('orderId', form);

  useEffect(() => {
    if (!isNewDocument) {
      fetchWZDocument();
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const orderIdForTransports = String(selectedOrderId || '').trim();

    if (!orderIdForTransports) {
      setTransports(linkedTransports);
      return () => { cancelled = true; };
    }

    const fetchTransports = async () => {
      try {
        const result = await fetchOrderTransports(orderIdForTransports);
        if (!cancelled) {
          setTransports(mergeWZTransportOptions(result.transports, linkedTransports));
        }
      } catch (error: any) {
        console.error('Error fetching order transports:', error);
        if (!cancelled) {
          setTransports(linkedTransports);
        }
      }
    };

    fetchTransports();
    return () => { cancelled = true; };
  }, [selectedOrderId, linkedTransports]);

  const fetchWZDocument = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/wz/${id}`);
      const doc = normalizeWZDocument(response.data, 'wz.detail.document');
      setWzDocument(doc);

      // Set form values
      form.setFieldsValue({
        ...doc,
        issueDate: doc.issueDate ? dayjs(doc.issueDate) : undefined,
        releaseDate: doc.releaseDate ? dayjs(doc.releaseDate) : undefined,
        receivedDate: doc.receivedDate ? dayjs(doc.receivedDate) : undefined,
        orderId: doc.orderId,
        transportIds: doc.transportIds || [],
      });
      const docLinkedTransports = Array.isArray(doc.linkedTransports) ? doc.linkedTransports : [];
      setLinkedTransports(docLinkedTransports);
      setTransports((previous) => mergeWZTransportOptions(previous, docLinkedTransports));

      // The delivery address input only shows when it actually diverges from
      // the recipient address — a WZ saved with delivery == recipient opens
      // with the toggle off (backend already treats empty deliveryAddress as
      // "use recipientAddress", see handleSave below).
      const loadedDeliveryAddress = (doc.deliveryAddress || '').trim();
      const loadedRecipientAddress = (doc.recipientAddress || '').trim();
      setDeliveryDifferent(loadedDeliveryAddress !== '' && loadedDeliveryAddress !== loadedRecipientAddress);

      // Attach the linked order's product dimensions so the per-line m²/m³
      // conversion can be shown/recomputed in the form (mirrors PDF resolution).
      setItems(doc.itemsLoaded
        ? attachWZItemDimensions(doc.items, doc.orderProducts)
        : [createEmptyItem()]);
    } catch (error: any) {
      message.error(error.response?.data?.error || t('wz:error.fetchFailed'));
      navigate('/app/wz');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(normalizeOrderOptions(response.data, 'wz.detail.orders'));
    } catch (error: any) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleOrderChange = async (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!wzDocument || orderId !== wzDocument.orderId) {
      setLinkedTransports([]);
    }
    form.setFieldsValue({
      transportIds: [],
      transportCompany: undefined,
      transportPrice: undefined,
      transportInvoiceNo: undefined,
      vehicleRegistration: undefined,
      driverName: undefined,
    });

    if (order) {
      form.setFieldsValue({
        recipientName: order.customer?.name || '',
        recipientAddress: order.customer?.address || '',
        recipientPhone: order.customer?.phone || '',
        recipientEmail: order.customer?.email || '',
        clientOrderNumber: order.clientOrderNumber || '',
      });

      // Set items from order products
      if (order.productsLoaded && order.products.length > 0) {
        setItems(mapOrderProductsToWZItems(order).map((item) => canEditSalePrices
          ? item
          : { ...item, unitPrice: 0, totalValue: 0 }));
      }
    }
  };

  const transportOptions = useMemo(() => (
    transports.map((transport) => buildWZTransportOption(transport, t))
  ), [transports, t]);

  const handleTransportSelect = (transportIds?: string[] | string) => {
    const { patch, snapshot } = buildWZTransportFormPatch(
      transports,
      transportIds,
      canEditPurchasePrices,
      form.getFieldValue('deliveryAddress'),
    );
    form.setFieldsValue({
      ...patch,
    });
    // A selected transport can carry its own delivery address distinct from
    // the recipient's — surface (and keep savable) the delivery input rather
    // than silently discarding the autofill behind a collapsed toggle.
    if (snapshot.deliveryAddress) {
      setDeliveryDifferent(true);
    }
  };

  const handleSave = async (values: any) => {
    if (!canEditDocument) {
      message.error(t('wz:error.saveFailed'));
      return;
    }

    if (!isNewDocument && wzDocument && !wzDocument.itemsLoaded) {
      message.error(t('wz:error.itemsUnavailable'));
      return;
    }

    setSaving(true);
    try {
      const safeItems = prepareWZItemsForSave(items).map((item) => {
        if (canEditSalePrices) {
          return item;
        }

        const { unitPrice, totalValue, ...safeItem } = item;
        return safeItem;
      });
      const data: CreateWZDto | UpdateWZDto = {
        ...values,
        wzNumber: values.wzNumber?.trim() || undefined,
        issueDate: values.issueDate?.format('YYYY-MM-DD'),
        releaseDate: values.releaseDate?.format('YYYY-MM-DD'),
        receivedDate: values.receivedDate ? values.receivedDate.format('YYYY-MM-DD') : null,
        transportIds: Array.isArray(values.transportIds) ? values.transportIds : [],
        items: safeItems,
        // The delivery input is hidden (and its stored value stale) whenever
        // the "different than registered" toggle is off — force it empty so
        // the backend falls back to recipientAddress instead of persisting a
        // leftover value from before the toggle was switched off.
        deliveryAddress: deliveryDifferent ? (values.deliveryAddress || '') : '',
      };
      delete data.status;
      delete (data as { costOfGoodsSold?: number }).costOfGoodsSold;
      if (!canEditPurchasePrices) {
        delete data.producerInvoice;
        delete data.purchaseInvoiceAmount;
        delete data.transportPrice;
        delete data.transportInvoiceNo;
      }

      if (isNewDocument) {
        const response = await api.post('/wz', data);
        message.success(t('wz:success.created'));
        const createdId = response?.data?.id;
        navigate(createdId ? `/app/wz/${createdId}` : '/app/wz');
      } else {
        await api.put(`/wz/${id}`, data);
        message.success(t('wz:success.updated'));
        // Stay on the document: drop ?mode=edit (back to view mode) and refresh.
        navigate(`/app/wz/${id}`, { replace: true });
        fetchWZDocument();
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.code === 'WZ_NUMBER_EXISTS'
          ? t('wz:error.wzNumberExists')
          : error.response?.data?.code === WZ_QUANTITY_INVALID_CODE
            ? t('wz:serverErrors.quantityPrecision')
          : (error.response?.data?.error || t('wz:error.saveFailed'))
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: WZStatus) => {
    try {
      await api.put(`/wz/${id}/status`, { status: newStatus });
      message.success(t('wz:success.statusUpdated'));
      fetchWZDocument();
    } catch (error: any) {
      const data = error.response?.data;
      if (data?.code === 'INSUFFICIENT_INVENTORY') {
        message.error(t('wz:error.insufficientInventory', {
          needed: data.needed,
          available: data.available,
        }));
      } else {
        message.error(data?.error || t('wz:error.statusUpdateFailed'));
      }
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/wz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${wzDocument?.wzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(t('wz:success.pdfDownloaded'));
    } catch (error: any) {
      message.error(error.response?.data?.error || t('wz:error.pdfGenerateFailed'));
    }
  };

  const addItem = () => {
    setItems([...items, createEmptyItem()]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof WZItem, value: any) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setItems(updatedItems);
  };

  /** Quantity and conversionUnit are decided together in the conversion modal,
   *  so they must land in one state update. `unit` stays untouched — it belongs
   *  to the catalog product, and the release consumes stock denominated in it. */
  const updateItemQuantity = (index: number, patch: WarehouseQuantityPatch) => {
    setItems((current) => current.map((item, itemIndex) => (
      itemIndex === index
        ? {
          ...item,
          quantity: patch.quantity,
          conversionUnit: patch.conversionUnit,
          ...(patch.unit ? { unit: patch.unit } : {}),
        }
        : item
    )));
  };

  /** Autofill a WZ row from a catalog variant selection. */
  const handleVariantSelect = (index: number, variantId: string | undefined, variant?: ProductVariant) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      ...buildWzVariantAutofill(updatedItems[index], variantId, variant),
    };
    setItems(updatedItems);
  };

  /** Free-text edit of the product name combobox detaches any catalog link —
   *  the operator is describing a product not in the catalog (or has edited
   *  away from a previously picked one). A catalog pick right after this
   *  (handleVariantSelect, via onSelectVariant) fires as a separate event and
   *  supersedes it with the full autofill, so the two can't race. */
  const handleProductNameTyped = (index: number, productName: string) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], productName, productVariantId: undefined };
    setItems(updatedItems);
  };

  const getStatusColor = (status: WZStatus) => {
    switch (status) {
      case WZStatus.DRAFT:
        return 'default';
      case WZStatus.CONFIRMED:
        return 'processing';
      case WZStatus.RELEASED:
        return 'success';
      case WZStatus.RECEIVED:
        return 'purple';
      case WZStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusOptions = () => {
    if (isNewDocument) {
      return [WZStatus.DRAFT];
    }

    return getEditableWZStatusOptions(wzDocument?.status, isNewDocument);
  };

  const statusTransitionOptions = wzDocument
    ? getEditableWZStatusOptions(wzDocument.status).filter(status => status !== wzDocument.status)
    : [];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/wz')}>
            {t('common:back')}
          </Button>
          <h1 style={{ margin: 0 }}>
            {isNewDocument ? t('wz:actions.create') : wzDocument?.wzNumber}
          </h1>
          {!isNewDocument && wzDocument && wzDocument.status && (
            <Tag color={getStatusColor(wzDocument.status)}>
              {t(`wz:status.${wzDocument.status.toLowerCase()}`)}
            </Tag>
          )}
        </Space>
        <Space>
          {!isNewDocument && !isEditMode && canEditDocument && (
            <Button onClick={() => navigate(`/app/wz/${id}?mode=edit`)}>
              {t('common:edit')}
            </Button>
          )}
          {!isNewDocument && canDownloadWZPdf && (
            <Button icon={<FilePdfOutlined />} onClick={handleDownloadPDF}>
              {t('wz:actions.downloadPDF')}
            </Button>
          )}
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        onFinishFailed={() => message.warning(t('wz:fillRequired'))}
        scrollToFirstError
        initialValues={{
          issueDate: dayjs(),
          releaseDate: dayjs(),
          status: WZStatus.DRAFT,
          issuedBy: currentUserDisplayName,
          receivedBy: currentUserDisplayName,
        }}
      >
        <Row gutter={24}>
          <Col span={24}>
            <Card title={t('wz:sections.basicInfo')} style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                {!isNewDocument && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label={t('wz:fields.wzNumber')}
                      name="wzNumber"
                      rules={[
                        { required: true, message: t('wz:validation.wzNumberRequired') },
                        { max: 50, message: t('wz:validation.wzNumberMax') },
                      ]}
                    >
                      <Input disabled={!isEditMode} maxLength={50} />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t('wz:fields.issueDate')}
                    name="issueDate"
                    rules={[{ required: true, message: t('wz:validation.issueDateRequired') }]}
                  >
                    <DatePicker style={{ width: '100%' }} disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t('wz:fields.releaseDate')}
                    name="releaseDate"
                    rules={[{ required: true, message: t('wz:validation.releaseDateRequired') }]}
                  >
                    <DatePicker style={{ width: '100%' }} disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t('wz:fields.receivedDate')}
                    name="receivedDate"
                  >
                    <DatePicker style={{ width: '100%' }} disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t('wz:fields.order')}
                    name="orderId"
                  >
                    <Select
                      placeholder={t('wz:placeholders.selectOrder')}
                      disabled={!isEditMode}
                      onChange={handleOrderChange}
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                      }
                    >
                        {orders.map(order => (
                          <Option key={order.id} value={order.id}>
                          {order.label || `${order.displayOrderNumber} - ${order.customerName || '-'}`}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t('wz:fields.status')}
                    name="status"
                  >
                    <Select disabled>
                      {getStatusOptions().map(status => (
                        <Option key={status} value={status}>
                          {t(`wz:status.${status.toLowerCase()}`)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t('wz:fields.clientOrderNumber')}
                    name="clientOrderNumber"
                  >
                    <Input disabled={!isEditMode} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col span={24}>
            <Card title={t('wz:sections.recipientAndDelivery')} style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  {/* Pick-or-type combobox: value IS recipientName, so a
                      loaded or freely-typed recipient always displays
                      natively. Picking a customer suggestion additionally
                      fills the rest of the recipient block via
                      buildRecipientFieldsFromCustomer; the fields stay
                      editable for a one-off recipient not in the list, and
                      picking an order afterwards can still override it. */}
                  <Form.Item
                    label={t('wz:fields.recipientName')}
                    name="recipientName"
                    rules={[{ required: true, message: t('wz:validation.recipientNameRequired') }]}
                  >
                    <CustomerNameCombobox
                      onSelectCustomer={(customer) => form.setFieldsValue(buildRecipientFieldsFromCustomer(customer))}
                      disabled={!isEditMode}
                      placeholder={t('wz:placeholders.selectCustomer')}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.recipientNIP')}
                    name="recipientNIP"
                  >
                    <Input disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.recipientAddress')}
                    name="recipientAddress"
                    rules={[{ required: true, message: t('wz:validation.recipientAddressRequired') }]}
                  >
                    <Input disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.recipientPhone')}
                    name="recipientPhone"
                  >
                    <Input disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.recipientEmail')}
                    name="recipientEmail"
                  >
                    <Input type="email" disabled={!isEditMode} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" plain>{t('wz:fields.deliveryAddress')}</Divider>

              <Row gutter={16}>
                <Col span={24}>
                  <Checkbox
                    checked={deliveryDifferent}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setDeliveryDifferent(on);
                      if (!on) {
                        form.setFieldsValue({ deliveryAddress: '' });
                      }
                    }}
                  >
                    {t('wz:delivery.differentFromRegistered')}
                  </Checkbox>
                </Col>
              </Row>

              {deliveryDifferent && (
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col xs={24}>
                    <Form.Item
                      name="deliveryAddress"
                    >
                      <TextArea
                        rows={3}
                        disabled={!isEditMode}
                        placeholder={t('wz:placeholders.deliveryAddress')}
                        aria-label={t('wz:fields.deliveryAddress')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>
          </Col>

          <Col span={24}>
            <Card
              title={t('wz:sections.items')}
              style={{ marginBottom: 24 }}
              className="wz-items-section"
              extra={
                isEditMode && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={addItem}>
                    {t('wz:actions.addItem')}
                  </Button>
                )
              }
            >
              <WZItemsList>
                {items.map((item, index) => {
                  const conversionLabel = formatConversionValue(
                    item.conversionUnit,
                    // normalizeQuantityUnit does not know the ProductUnit enum, so
                    // the source unit must already be a canonical line code.
                    { ...item, unit: getWarehouseLineUnit(item) },
                    Number(item.quantity) || 0,
                  );

                  return (
                    <WZItemCard
                      key={`${item.id || item.productVariantId || 'item'}-${index}`}
                      index={index}
                      positionLabel={t('wz:itemFields.position', { number: index + 1 })}
                      productLabel={t('wz:fields.productName')}
                      product={isEditMode ? (
                        <ProductNameCombobox
                          value={item.productName || ''}
                          onChange={(productName) => handleProductNameTyped(index, productName)}
                          onSelectVariant={(variantId, variant) => handleVariantSelect(index, variantId, variant)}
                          placeholder={t('wz:placeholders.productName')}
                        />
                      ) : item.productName}
                      action={isEditMode ? (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                          aria-label={t('common:delete')}
                        />
                      ) : undefined}
                    >
                      <WZItemField label={t('wz:fields.dimensions')} wide>
                        {formatLineDimensions(item)}
                      </WZItemField>
                      <WZItemField
                        label={t('wz:fields.quantity')}
                        wide={isEditMode}
                        emphasis
                      >
                        {isEditMode ? (
                          <WarehouseQuantityField
                            line={item}
                            onChange={(patch) => updateItemQuantity(index, patch)}
                            maxQuantity={WZ_QUANTITY_MAX}
                            unitEditable
                            unitLabel={t('wz:fields.unit')}
                            maxConvertedQuantity={WAREHOUSE_CONVERTED_QUANTITY_MAX}
                            noDimensionsLabel={t('wz:conversion.noDimensions')}
                          />
                        ) : `${item.quantity} ${formatUnit(item.unit, i18n.language)}`}
                      </WZItemField>
                      {!isEditMode && (
                        <WZItemField label={t('wz:fields.conversion')}>
                          {item.conversionUnit ? (conversionLabel || '—') : '—'}
                        </WZItemField>
                      )}
                      <WZItemField label={t('wz:fields.notes')} wide>
                        {isEditMode ? (
                          <Input
                            value={item.notes}
                            onChange={(event) => updateItem(index, 'notes', event.target.value)}
                            placeholder={t('wz:fields.notes')}
                          />
                        ) : item.notes}
                      </WZItemField>
                    </WZItemCard>
                  );
                })}
              </WZItemsList>
            </Card>
          </Col>

          <Col span={24}>
            <Card style={{ marginBottom: 24 }} styles={{ body: { padding: 0 } }}>
              <Collapse
                ghost
                defaultActiveKey={[]}
                items={[
                  {
                    key: 'transport',
                    label: (
                      <Space size={8}>
                        <span style={{ fontWeight: 600 }}>{t('wz:sections.transport')}</span>
                        <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
                          ({t('wz:transport.optionalTag')})
                        </Typography.Text>
                      </Space>
                    ),
                    children: (
                      <Row gutter={16}>
                        <Col xs={24}>
                          <Form.Item
                            label={t('wz:fields.linkedTransports')}
                            name="transportIds"
                          >
                            <Select
                              allowClear
                              showSearch
                              mode="multiple"
                              disabled={!isEditMode}
                              placeholder={t('wz:placeholders.selectTransport')}
                              options={transportOptions}
                              onChange={handleTransportSelect}
                              optionFilterProp="label"
                              maxTagCount="responsive"
                            />
                          </Form.Item>
                          <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
                            {t('wz:transport.autofillHint')}
                          </Typography.Paragraph>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            label={t('wz:fields.transportCompany')}
                            name="transportCompany"
                          >
                            <Input disabled={!isEditMode} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            label={t('wz:fields.driverName')}
                            name="driverName"
                          >
                            <Input disabled={!isEditMode} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            label={t('wz:fields.vehicleRegistration')}
                            name="vehicleRegistration"
                          >
                            <Input disabled={!isEditMode} />
                          </Form.Item>
                        </Col>
                        {canEditPurchasePrices && (
                          <>
                            <Col xs={24} sm={12} md={8}>
                              <Form.Item
                                label={t('wz:fields.transportPrice')}
                                name="transportPrice"
                              >
                                <Input type="number" step="0.01" min={0} disabled={!isEditMode} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                              <Form.Item
                                label={t('wz:fields.transportInvoiceNo')}
                                name="transportInvoiceNo"
                              >
                                <Input disabled={!isEditMode} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                              <Form.Item
                                label={t('wz:fields.producerInvoice')}
                                name="producerInvoice"
                              >
                                <Input disabled={!isEditMode} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                              <Form.Item
                                label={t('wz:fields.purchaseInvoiceAmount')}
                                name="purchaseInvoiceAmount"
                              >
                                <Input type="number" step="0.01" min={0} disabled={!isEditMode} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                              <Form.Item
                                label={t('wz:fields.costOfGoodsSold')}
                                name="costOfGoodsSold"
                              >
                                <Input type="number" step="0.01" disabled />
                              </Form.Item>
                            </Col>
                          </>
                        )}
                        <Col span={24}>
                          <Form.Item
                            label={t('wz:fields.transportNotes')}
                            name="transportNotes"
                          >
                            <TextArea rows={3} disabled={!isEditMode} />
                          </Form.Item>
                        </Col>
                      </Row>
                    ),
                  },
                ]}
              />
            </Card>
          </Col>

          <Col span={24}>
            <Card title={t('wz:sections.signatureAndNotes')} style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.issuedBy')}
                    name="issuedBy"
                    rules={[
                      { required: true, message: t('wz:validation.issuedByRequired') },
                      { max: 100, message: t('wz:validation.personNameMax') },
                    ]}
                  >
                    <Input disabled={!isEditMode} placeholder={t('wz:placeholders.issuedBy')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.receivedBy')}
                    name="receivedBy"
                    rules={[{ max: 100, message: t('wz:validation.personNameMax') }]}
                  >
                    <Input disabled={!isEditMode} placeholder={t('wz:placeholders.receivedBy')} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    label={t('wz:fields.notes')}
                    name="notes"
                  >
                    <TextArea rows={4} disabled={!isEditMode} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {isEditMode && (
            <Col span={24}>
              <Space>
                <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                  {t('common:save')}
                </Button>
                <Button onClick={() => navigate('/app/wz')}>
                  {t('common:cancel')}
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Form>

      {!isNewDocument && !isEditMode && wzDocument && canEdit && (
        <Card title={t('wz:sections.statusActions')} style={{ marginTop: 24 }}>
          <Space>
            {statusTransitionOptions.includes(WZStatus.CONFIRMED) && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => handleStatusChange(WZStatus.CONFIRMED)}
              >
                {t('wz:actions.confirm')}
              </Button>
            )}
            {statusTransitionOptions.includes(WZStatus.RELEASED) && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => handleStatusChange(WZStatus.RELEASED)}
              >
                {t('wz:actions.markAsReleased')}
              </Button>
            )}
            {statusTransitionOptions.includes(WZStatus.RECEIVED) && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => handleStatusChange(WZStatus.RECEIVED)}
              >
                {t('wz:actions.markAsReceived')}
              </Button>
            )}
            {statusTransitionOptions.includes(WZStatus.CANCELLED) && (
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => handleStatusChange(WZStatus.CANCELLED)}
              >
                {t('wz:actions.cancel')}
              </Button>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default WZDetail;
